import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Camera,
  Video,
  FileText,
  Upload,
  X,
  Calendar,
  MapPin,
  User,
  Mail,
  ChevronLeft,
  ChevronRight,
  Filter,
  Grid3X3,
  Clock,
  Heart,
  Play,
  Image as ImageIcon,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

// Content type icons
const contentTypeIcons = {
  photo: Camera,
  video: Video,
  story: FileText
};

// Category colors
const categoryColors = {
  fire_2021: 'bg-red-500/10 text-red-600 border-red-500/30',
  recovery: 'bg-green-500/10 text-green-600 border-green-500/30',
  before_after: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  community_response: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  nature: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  testimonial: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  historical: 'bg-slate-500/10 text-slate-600 border-slate-500/30'
};

// Lightbox component
function Lightbox({ item, onClose, onPrev, onNext, hasPrev, hasNext }) {
  const { t } = useLanguage();
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white z-50 p-2"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Navigation buttons */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50 p-2 bg-black/30 rounded-full"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-50 p-2 bg-black/30 rounded-full"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Content */}
      <div
        className="max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Media */}
        <div className="relative flex-1 flex items-center justify-center mb-4">
          {item.content_type === 'photo' && item.file_url && (
            <img
              src={`${import.meta.env.REACT_APP_BACKEND_URL}${item.file_url}`}
              alt={item.description || 'Community submission'}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          )}
          {item.content_type === 'video' && item.file_url && (
            <video
              src={`${import.meta.env.REACT_APP_BACKEND_URL}${item.file_url}`}
              controls
              className="max-w-full max-h-[70vh] rounded-lg"
            />
          )}
          {item.content_type === 'story' && (
            <div className="bg-card rounded-lg p-8 max-w-2xl max-h-[70vh] overflow-y-auto">
              <FileText className="w-12 h-12 text-primary mb-4" />
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                {item.story_text}
              </p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-card/90 backdrop-blur rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-foreground text-lg">{item.name}</h3>
              <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
            </div>
            <Badge className={categoryColors[item.category]}>
              {item.category?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
            {item.event_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {item.event_date}
              </span>
            )}
            {item.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {item.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Submission Form Component
function SubmissionForm({ categories, onSubmitSuccess }) {
  const { t } = useLanguage();
  const [contentType, setContentType] = useState('photo');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    event_date: '',
    location: '',
    category: '',
    description: '',
    story_text: ''
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = {
      photo: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      video: ['video/mp4', 'video/webm', 'video/quicktime']
    };
    
    if (!validTypes[contentType]?.includes(selectedFile.type)) {
      toast.error(`Invalid file type for ${contentType}`);
      return;
    }

    // Validate file size (50MB max for videos, 10MB for photos)
    const maxSize = contentType === 'video' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      toast.error(`File too large. Maximum size: ${contentType === 'video' ? '50MB' : '10MB'}`);
      return;
    }

    setFile(selectedFile);

    // Create preview
    if (contentType === 'photo') {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result);
      reader.readAsDataURL(selectedFile);
    } else if (contentType === 'video') {
      setFilePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if ((contentType === 'photo' || contentType === 'video') && !file) {
      toast.error(`Please select a ${contentType} to upload`);
      return;
    }

    if (contentType === 'story' && !formData.story_text) {
      toast.error('Please write your story');
      return;
    }

    setSubmitting(true);

    try {
      // Convert file to base64 if present
      let fileData = null;
      if (file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target?.result?.split(',')[1]);
          reader.readAsDataURL(file);
        });
      }

      const payload = {
        content_type: contentType,
        name: formData.name,
        email: formData.email,
        event_date: formData.event_date || null,
        location: formData.location || null,
        category: formData.category,
        description: formData.description || null,
        story_text: contentType === 'story' ? formData.story_text : null,
        file_data: fileData,
        file_name: file?.name || null,
        file_type: file?.type || null
      };

      const response = await fetch(`${API}/community/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }

      setSubmitted(true);
      toast.success('Thank you! Your submission is pending review.');
      onSubmitSuccess?.();
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        event_date: '',
        location: '',
        category: '',
        description: '',
        story_text: ''
      });
      setFile(null);
      setFilePreview(null);

    } catch (err) {
      toast.error(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Submission Received!
        </h3>
        <p className="text-muted-foreground mb-6">
          Thank you for sharing. Your content will be reviewed and added to our community memory.
        </p>
        <Button onClick={() => setSubmitted(false)} variant="outline">
          Submit Another
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="submission-form">
      {/* Content Type Selection */}
      <div>
        <Label className="text-base font-medium mb-3 block">What would you like to share?</Label>
        <div className="grid grid-cols-3 gap-3">
          {['photo', 'video', 'story'].map((type) => {
            const Icon = contentTypeIcons[type];
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setContentType(type);
                  setFile(null);
                  setFilePreview(null);
                }}
                className={`p-4 rounded-lg border-2 transition-all ${
                  contentType === type
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`content-type-${type}`}
              >
                <Icon className={`w-6 h-6 mx-auto mb-2 ${contentType === type ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-medium ${contentType === type ? 'text-primary' : 'text-muted-foreground'}`}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* File Upload (for photo/video) */}
      {(contentType === 'photo' || contentType === 'video') && (
        <div>
          <Label className="mb-2 block">
            Upload {contentType} <span className="text-red-500">*</span>
          </Label>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
          >
            {filePreview ? (
              <div className="relative">
                {contentType === 'photo' ? (
                  <img
                    src={filePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg"
                  />
                ) : (
                  <video
                    src={filePreview}
                    className="max-h-48 mx-auto rounded-lg"
                    controls
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setFilePreview(null);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  Click or drag to upload your {contentType}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {contentType === 'photo' ? 'JPG, PNG, GIF, WebP (max 10MB)' : 'MP4, WebM, MOV (max 50MB)'}
                </p>
                <input
                  type="file"
                  accept={contentType === 'photo' ? 'image/*' : 'video/*'}
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
              </label>
            )}
          </div>
        </div>
      )}

      {/* Story Text (for stories) */}
      {contentType === 'story' && (
        <div>
          <Label htmlFor="story_text">
            Your Story <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="story_text"
            value={formData.story_text}
            onChange={(e) => setFormData({ ...formData, story_text: e.target.value })}
            placeholder="Share your memory, experience, or story about Epuyén..."
            className="min-h-[200px]"
            data-testid="story-text-input"
          />
        </div>
      )}

      {/* Personal Info */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">
            Your Name <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              className="pl-10"
              required
              data-testid="name-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="email">
            Email <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              className="pl-10"
              required
              data-testid="email-input"
            />
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="event_date">Date of Event/Photo</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="event_date"
              type="date"
              value={formData.event_date}
              onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              className="pl-10"
              data-testid="date-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="location">Location</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Lago Epuyén"
              className="pl-10"
              data-testid="location-input"
            />
          </div>
        </div>
      </div>

      {/* Category */}
      <div>
        <Label htmlFor="category">
          Category <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.category}
          onValueChange={(v) => setFormData({ ...formData, category: v })}
        >
          <SelectTrigger data-testid="category-select">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.code} value={cat.code}>
                {cat.name_en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Add any additional context or description..."
          className="min-h-[100px]"
          data-testid="description-input"
        />
      </div>

      {/* Submit */}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full"
        size="lg"
        data-testid="submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            Submit for Review
          </>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        Your submission will be reviewed before being published to the community gallery.
      </p>
    </form>
  );
}

// Gallery Grid Component
function GalleryGrid({ submissions, onItemClick }) {
  if (!submissions?.length) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">No submissions found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {submissions.map((item, index) => (
        <motion.div
          key={item.submission_id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => onItemClick(item)}
          className="group cursor-pointer"
          data-testid={`gallery-item-${item.submission_id}`}
        >
          <Card className="overflow-hidden hover:shadow-lg transition-all">
            <div className="relative aspect-square bg-muted">
              {item.content_type === 'photo' && item.file_url && (
                <img
                  src={`${import.meta.env.REACT_APP_BACKEND_URL}${item.file_url}`}
                  alt={item.description || 'Community photo'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              )}
              {item.content_type === 'video' && (
                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                  {item.file_url && (
                    <video
                      src={`${import.meta.env.REACT_APP_BACKEND_URL}${item.file_url}`}
                      className="w-full h-full object-cover"
                      muted
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-5 h-5 text-white ml-1" />
                    </div>
                  </div>
                </div>
              )}
              {item.content_type === 'story' && (
                <div className="w-full h-full flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-primary/5">
                  <p className="text-sm text-foreground line-clamp-4 text-center">
                    &ldquo;{item.story_text?.substring(0, 150)}...&rdquo;
                  </p>
                </div>
              )}
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              {/* Content type badge */}
              <div className="absolute top-2 left-2">
                {item.content_type === 'photo' && <Camera className="w-4 h-4 text-white drop-shadow-lg" />}
                {item.content_type === 'video' && <Video className="w-4 h-4 text-white drop-shadow-lg" />}
                {item.content_type === 'story' && <FileText className="w-4 h-4 text-white drop-shadow-lg" />}
              </div>
            </div>
            
            <div className="p-3">
              <p className="font-medium text-foreground text-sm truncate">{item.name}</p>
              <div className="flex items-center justify-between mt-1">
                <Badge variant="outline" className={`text-xs ${categoryColors[item.category]}`}>
                  {item.category?.replace('_', ' ')}
                </Badge>
                {item.event_date && (
                  <span className="text-xs text-muted-foreground">{item.event_date}</span>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

// Main Page Component
export default function CommunityMemoriaPage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('gallery');
  const [categories, setCategories] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [timeline, setTimeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/community/categories`);
        const data = await res.json();
        setCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch submissions
  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (contentTypeFilter !== 'all') params.append('content_type', contentTypeFilter);
      
      const res = await fetch(`${API}/community/approved?${params}`);
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, contentTypeFilter]);

  // Fetch timeline
  const fetchTimeline = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/timeline`);
      const data = await res.json();
      setTimeline(data.timeline || {});
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
    fetchTimeline();
  }, [fetchSubmissions, fetchTimeline]);

  // Lightbox navigation
  const handlePrev = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedItem(submissions[selectedIndex - 1]);
    }
  };

  const handleNext = () => {
    if (selectedIndex < submissions.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedItem(submissions[selectedIndex + 1]);
    }
  };

  const handleItemClick = (item) => {
    const index = submissions.findIndex(s => s.submission_id === item.submission_id);
    setSelectedIndex(index);
    setSelectedItem(item);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="community-memoria-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1920&q=80"
            alt="Forest memory"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 pt-16">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Heart className="w-3.5 h-3.5" />
              Community Memory
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Memoria Colectiva de Epuyén
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              A living archive of our community&apos;s history, resilience, and hope. 
              Share your photos, videos, and stories to help preserve our collective memory.
            </p>
            
            <Link to="/community">
              <Button variant="outline" className="mr-3">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Community
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="gallery" data-testid="tab-gallery">
                  <Grid3X3 className="w-4 h-4 mr-2" />
                  Gallery
                </TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline">
                  <Clock className="w-4 h-4 mr-2" />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="submit" data-testid="tab-submit">
                  <Upload className="w-4 h-4 mr-2" />
                  Submit
                </TabsTrigger>
              </TabsList>

              {/* Filters (only for gallery) */}
              {activeTab === 'gallery' && (
                <div className="flex gap-2">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="category-filter">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.code} value={cat.code}>
                          {cat.name_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
                    <SelectTrigger className="w-[120px]" data-testid="type-filter">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="photo">Photos</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="story">Stories</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Gallery Tab */}
            <TabsContent value="gallery" data-testid="gallery-content">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <GalleryGrid
                  submissions={submissions}
                  onItemClick={handleItemClick}
                />
              )}
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" data-testid="timeline-content">
              <div className="space-y-8">
                {Object.keys(timeline).length === 0 ? (
                  <div className="text-center py-16">
                    <Clock className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No timeline entries yet</p>
                  </div>
                ) : (
                  Object.entries(timeline)
                    .sort((a, b) => b[0].localeCompare(a[0]))
                    .map(([dateKey, items]) => (
                      <div key={dateKey} className="relative">
                        <div className="sticky top-20 z-10 bg-background/95 backdrop-blur py-2 mb-4">
                          <h3 className="text-lg font-semibold text-foreground">
                            {dateKey === 'Unknown' ? 'Date Unknown' : dateKey}
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-4 border-l-2 border-primary/20">
                          {items.map((item) => (
                            <Card
                              key={item.submission_id}
                              className="overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                              onClick={() => handleItemClick(item)}
                            >
                              <div className="aspect-square bg-muted relative">
                                {item.content_type === 'photo' && item.file_url && (
                                  <img
                                    src={`${import.meta.env.REACT_APP_BACKEND_URL}${item.file_url}`}
                                    alt={item.description}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                                {item.content_type === 'video' && (
                                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                                    <Play className="w-8 h-8 text-white" />
                                  </div>
                                )}
                                {item.content_type === 'story' && (
                                  <div className="w-full h-full flex items-center justify-center p-3">
                                    <p className="text-xs text-center line-clamp-5">
                                      {item.story_text?.substring(0, 100)}...
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="p-2">
                                <p className="text-sm font-medium truncate">{item.name}</p>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </TabsContent>

            {/* Submit Tab */}
            <TabsContent value="submit" data-testid="submit-content">
              <Card className="max-w-2xl mx-auto">
                <div className="p-6 sm:p-8">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      Share Your Memory
                    </h2>
                    <p className="text-muted-foreground">
                      Help us build our collective memory. Upload photos, videos, or write your story.
                    </p>
                  </div>
                  
                  <SubmissionForm
                    categories={categories}
                    onSubmitSuccess={() => {
                      fetchSubmissions();
                      fetchTimeline();
                    }}
                  />
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedItem && (
          <Lightbox
            item={selectedItem}
            onClose={() => {
              setSelectedItem(null);
              setSelectedIndex(-1);
            }}
            onPrev={handlePrev}
            onNext={handleNext}
            hasPrev={selectedIndex > 0}
            hasNext={selectedIndex < submissions.length - 1}
          />
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-12 px-4 eco-gradient text-white/80">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Heart className="w-5 h-5" />
              </div>
              <span className="font-semibold text-lg text-white">Epuyén Renace</span>
            </div>
            <p className="text-sm opacity-70">
              © {new Date().getFullYear()} Epuyén Renace. Preserving our collective memory.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
